import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Lien de réinitialisation invalide. Veuillez demander un nouveau lien.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/connexion');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2bde1c9b-86e8-45d3-80bd-0f0476a263ab/artifacts/ur3i0a8o_BAckground.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="reset-password-success"
      >
        <div className="absolute inset-0 bg-[#0A0A0B]/90" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow max-w-md w-full text-center"
        >
          <div className="inline-block p-4 bg-[#00FF88]/10 rounded-full mb-6">
            <CheckCircle className="h-16 w-16 text-[#00FF88]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Mot de passe réinitialisé !</h2>
          <p className="text-[#B8B8BA] mb-6">
            Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
          </p>
          <p className="text-[#00FFFF] text-sm">Redirection vers la connexion...</p>
        </motion.div>
      </div>
    );
  }

  if (!token) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2bde1c9b-86e8-45d3-80bd-0f0476a263ab/artifacts/ur3i0a8o_BAckground.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="reset-password-error"
      >
        <div className="absolute inset-0 bg-[#0A0A0B]/90" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow max-w-md w-full text-center"
        >
          <div className="inline-block p-4 bg-[#FF3B5C]/10 rounded-full mb-6">
            <XCircle className="h-16 w-16 text-[#FF3B5C]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Lien invalide</h2>
          <p className="text-[#B8B8BA] mb-6">{error}</p>
          <Link to="/mot-de-passe-oublie" className="btn-primary">
            Demander un nouveau lien
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2bde1c9b-86e8-45d3-80bd-0f0476a263ab/artifacts/ur3i0a8o_BAckground.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="reset-password-page"
    >
      <div className="absolute inset-0 bg-[#0A0A0B]/90" />
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Nouveau mot de passe</h1>
            <p className="text-[#B8B8BA]">
              Choisissez un nouveau mot de passe sécurisé pour votre compte.
            </p>
          </div>

          {error && (
            <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 text-[#FF3B5C] p-4 rounded-lg mb-6 flex items-center space-x-3">
              <XCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[#B8B8BA] text-sm font-medium mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6B6B6E]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-[#222226] text-white pl-12 pr-12 py-3 rounded-lg border border-[#3A3A3D] focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="reset-password-new"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#6B6B6E] hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-[#6B6B6E] text-xs mt-1">Minimum 8 caractères</p>
            </div>

            <div>
              <label className="block text-[#B8B8BA] text-sm font-medium mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6B6B6E]" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full bg-[#222226] text-white pl-12 pr-12 py-3 rounded-lg border border-[#3A3A3D] focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="reset-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#6B6B6E] hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 flex items-center justify-center"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent" />
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link
              to="/connexion"
              className="text-[#00FFFF] hover:underline inline-flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Retour à la connexion</span>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
